import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../ui";
import { useNavigate } from "react-router-dom";
import ForgotPasswordModal from "../../Modals/ForgotPasswordModal";
import { api, setAdminSession } from "../../../services";
import { saveFlashMessage } from "../../../utils/flashMessage";
import { useTranslation } from "react-i18next";
import { translateAuthApiMessage } from "../../../utils/translateAuthApiMessage";
import logo from "../../../assets/logos/logo.png";
import AuthPageLayout from "../../AuthPageLayout";

function AdminLogin() {
  const { t } = useTranslation("translation");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();
  const didRun = useRef(false);

  const handleAdminLogin = async () => {
    setLoading(true);
    setLoginError(null);
    try {
      const response = await api.post("/admin/login", { email, password });

      setAdminSession(response.data.token, response.data.user);
      localStorage.setItem("user_type", response.data.user_type);
      localStorage.removeItem("token", response.data.token);
      const params = new URLSearchParams(window.location.search);
      const redirectPath = params.get("redirect");
      localStorage.setItem("token", response.data.token);
      window.dispatchEvent(new Event("userLogin"));
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      } else {
        navigate("/admin/user-list", { replace: true });
      }
    } catch (err) {
      setLoginError(err.response?.data?.message ?? "");
    } finally {
      setLoading(false);
    }
  };

  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      if (payload.exp && payload.exp < currentTime) {
        return true;
      }
      return false;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const token = localStorage.getItem("token");
    const adminToken = localStorage.getItem("adminToken");

    if (adminToken && !isTokenExpired(adminToken) && token && !isTokenExpired(token)) {
      saveFlashMessage(`${t("You are already logged in.")}`, "info");
      navigate("/dashboard", { replace: true });
      return;
    }

    if (adminToken && !isTokenExpired(adminToken)) {
      saveFlashMessage(`${t("You are already logged in.")}`, "info");
      navigate("/admin/user-list", { replace: true });
    } else if (token && !isTokenExpired(token)) {
      saveFlashMessage(`${t("You are already logged in.")}`, "info");
      navigate("/dashboard", { replace: true });
    }
  }, [navigate, t]);

  const error =
    loginError == null
      ? ""
      : translateAuthApiMessage(loginError, t) || t("An error occurred during login.");

  return (
    <AuthPageLayout>
      <div className="auth-page-card__logo">
        <img src={logo} alt="Loops" />
      </div>
      <h1 className="auth-page-card__title">{t("Admin Login")}</h1>

      <form
        className="auth-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleAdminLogin();
        }}
        noValidate
      >
        <div className="auth-field">
          <input
            id="admin-email"
            type="email"
            autoComplete="username"
            placeholder={t("email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="auth-field">
          <input
            id="admin-password"
            type="password"
            autoComplete="current-password"
            placeholder={t("Password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error ? <p className="auth-form-error">{error}</p> : null}

        <Button
          type="submit"
          color="primary"
          size="md"
          className="auth-submit"
          disabled={loading}
        >
          {loading ? t("Logging in...") : t("Log In")}
        </Button>
      </form>

      <div className="auth-links-row" style={{ justifyContent: "center" }}>
        <span className="auth-link-muted" style={{ cursor: "default" }}>
          {t("Forgot Password")}?{" "}
        </span>
        <button
          type="button"
          className="auth-link-primary"
          onClick={() => setShowForgotPassword(true)}
        >
          {t("Reset it here")}
        </button>
      </div>

      <ForgotPasswordModal
        show={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </AuthPageLayout>
  );
}

export default AdminLogin;
